import { DateTime } from "luxon";
import { FinraHolidays } from "../app/data/dataProvider";
import { roundToDecimals } from "./number.utils";

export function getFinraEarlyCloseSet(finraHolidays: FinraHolidays) {
  return finraHolidays 
    ? new Set(Object.getOwnPropertyNames(finraHolidays.early_close).map(d => DateTime.fromISO(d, { zone: 'America/New_York' }).toMillis())) 
    : new Set<number>()
}

export function getFinraHolidaySet(finraHolidays: FinraHolidays) {
  return finraHolidays 
    ? new Set(finraHolidays.holiday.map(d => DateTime.fromISO(d, { zone: 'America/New_York' }).toMillis())) 
    : new Set<number>()
}

export function getEndOfDayMs(dateMs: number, finraHolidays: FinraHolidays, earlyCloseSet: Set<number>) {
  return earlyCloseSet.has(dateMs)
      ? DateTime.fromISO(finraHolidays!.early_close[DateTime.fromMillis(dateMs, { zone: 'America/New_York' }).toISODate()!]).plus({ hour: 1 }).toMillis()
      : DateTime.fromMillis(dateMs, { zone: 'America/New_York' }).set({ hour: 18 }).toMillis()
}

const TRADING_DAY_OPEN_HOUR = 8; // regular session opens at 8:00 AM ET
// small slice of x-axis width given to each closed period (overnight/weekend/holiday) so a day's
// close and the next session's open get distinct x values -- a gentle slope between sessions instead
// of a vertical jump at one x. Kept small so non-trading time stays mostly collapsed.
const SESSION_GAP_MS = 60 * 60 * 1000; // 1 hour

// Returns a predicate reporting whether an epoch-ms instant falls within regular
// trading hours in America/New_York: a weekday that is not a FINRA holiday, from
// 8:00 AM ET up to one hour past the day's close. That end boundary is exactly
// getEndOfDayMs (6 PM ET on normal days; one hour past the FINRA early-close time on
// early-close days), since the codebase models the end-of-day cutoff as one hour
// after the close. Until the FINRA holiday calendar has loaded (finraHolidays ===
// null) every instant is accepted, so callers behave as before and tighten up once
// the calendar arrives.
export function getIsDuringTradingHours(finraHolidays: FinraHolidays): (ms: number) => boolean {
  if (!finraHolidays) {
    return () => true;
  }
  const holidaySet = getFinraHolidaySet(finraHolidays);
  const earlyCloseSet = getFinraEarlyCloseSet(finraHolidays);
  return (ms: number) => {
    const dateTime = DateTime.fromMillis(ms, { zone: 'America/New_York' });
    if (dateTime.weekday > 5) {
      return false; // weekend
    }
    const startOfDayMs = dateTime.startOf('day').toMillis();
    if (holidaySet.has(startOfDayMs)) {
      return false; // FINRA holiday
    }
    const openMs = dateTime.startOf('day').set({ hour: TRADING_DAY_OPEN_HOUR }).toMillis();
    const closeMs = getEndOfDayMs(startOfDayMs, finraHolidays, earlyCloseSet); // one hour past close
    return ms >= openMs && ms <= closeMs;
  };
}

const MAX_SNAP_BACK_DAYS = 10; // a weekend plus any adjacent FINRA holiday cluster is well under this

// Snap an out-of-hours instant back to the same wall-clock time on the most recent trading day (e.g.
// Sunday 17:00 -> the prior Friday 17:00). Returns `ms` unchanged when it's already in trading hours,
// or null when no trading day within ~2 weeks has that wall-clock time in hours (e.g. an overnight
// 02:00 sample) -- the caller should then drop it. This keeps day/week/month-stepped charts from
// silently emptying when every sample lands on one weekday that happens to be a weekend (e.g. the 3Y
// chart's weekly step when "now - 3 years" is a Sunday).
export function snapToTradingHours(ms: number, isDuringTradingHours: (ms: number) => boolean): number | null {
  if (isDuringTradingHours(ms)) {
    return ms;
  }
  let day = DateTime.fromMillis(ms, { zone: 'America/New_York' });
  for (let i = 0; i < MAX_SNAP_BACK_DAYS; i++) {
    day = day.minus({ days: 1 }); // calendar-day step preserves the ET wall-clock time, incl. across DST
    const candidate = day.toMillis();
    if (isDuringTradingHours(candidate)) {
      return candidate;
    }
  }
  return null;
}

// The close (end-of-day cutoff = getEndOfDayMs: 6 PM ET, or 1h past an early close) of the most recent
// trading session whose close is at or before `ms`; an in-hours instant (before today's close) thus
// resolves to the PREVIOUS session's close. Pure form of the data provider's getPrevEoD -- used to
// anchor intraday charts to the last session when the market is closed. Null until the calendar loads.
export function getPreviousTradingClose(ms: number, finraHolidays: FinraHolidays): number | null {
  if (!finraHolidays) {
    return null;
  }
  const holidaySet = getFinraHolidaySet(finraHolidays);
  const earlyCloseSet = getFinraEarlyCloseSet(finraHolidays);
  let cursor = DateTime.fromMillis(ms, { zone: 'America/New_York' });
  let startOfDayMs = cursor.startOf('day').toMillis();
  // walk back to the most recent weekday/non-holiday whose close is at/before the cursor. Compare
  // against the day's actual end-of-day cutoff (getEndOfDayMs: 6 PM ET, or 1h past an early close) --
  // so an early-close afternoon resolves to that day's own early close rather than rolling back a day.
  for (
    let guard = 0;
    cursor.weekday > 5 || holidaySet.has(startOfDayMs) || cursor.toMillis() < getEndOfDayMs(startOfDayMs, finraHolidays, earlyCloseSet);
    guard++
  ) {
    if (guard >= 25000) {
      throw new Error(`getPreviousTradingClose: walked back past the 25000-day guard from ${ms}`);
    }
    cursor = cursor.minus({ days: 1 }).endOf('day'); // last ms of the previous day (after any close, so it settles)
    startOfDayMs = cursor.startOf('day').toMillis();
  }
  return getEndOfDayMs(startOfDayMs, finraHolidays, earlyCloseSet);
}

// regular-hours [open, close] sessions overlapping [startMs, endMs], clamped to that window: 8 AM ET
// open through one hour past the close (= getEndOfDayMs, i.e. 6 PM ET normal / 1h past early close).
// The caller supplies the prebuilt holiday/early-close sets so this can run repeatedly without
// rebuilding them.
function tradingSessionsBetween(
  startMs: number,
  endMs: number,
  finraHolidays: FinraHolidays,
  holidaySet: Set<number>,
  earlyCloseSet: Set<number>
): [number, number][] {
  const sessions: [number, number][] = [];
  const lastDay = DateTime.fromMillis(endMs, { zone: 'America/New_York' }).startOf('day');
  let day = DateTime.fromMillis(startMs, { zone: 'America/New_York' }).startOf('day');
  for (let guard = 0; day <= lastDay; day = day.plus({ days: 1 }), guard++) {
    if (guard >= 25000) {
      // fail fast (~68 years) instead of silently returning a truncated session list, which would
      // corrupt the x-axis mapping/stepping; a span this large signals a bad date range upstream
      throw new Error(`tradingSessionsBetween: range exceeds the 25000-day guard [${startMs}, ${endMs}]`);
    }
    const dayStartMs = day.toMillis();
    if (day.weekday > 5 || holidaySet.has(dayStartMs)) {
      continue; // weekend or FINRA holiday
    }
    const openMs = Math.max(day.set({ hour: TRADING_DAY_OPEN_HOUR }).toMillis(), startMs);
    const closeMs = Math.min(getEndOfDayMs(dayStartMs, finraHolidays, earlyCloseSet), endMs); // one hour past close
    if (closeMs > openMs) {
      sessions.push([openMs, closeMs]);
    }
  }
  return sessions;
}

// Returns a function that places `steps` timestamps within regular trading hours, distributed evenly
// across *trading time* between startMs and endMs. Closed periods (nights, weekends, FINRA holidays)
// are collapsed, so every session is densely and evenly covered with no large gaps -- unlike spacing
// points evenly in calendar time and then discarding the out-of-hours ones. Session boundaries match
// getIsDuringTradingHours. The interval endpoints are excluded, and a fully-closed span returns [].
// Until the FINRA calendar has loaded (finraHolidays === null) it falls back to even calendar spacing.
export function getTradingHourStepper(finraHolidays: FinraHolidays): (startMs: number, endMs: number, steps: number) => number[] {
  if (!finraHolidays) {
    return (startMs, endMs, steps) => {
      if (steps <= 0 || endMs <= startMs) {
        return [];
      }
      const msStep = (endMs - startMs) / (steps + 1);
      return Array.from({ length: steps }, (_, i) => Math.round(startMs + (i + 1) * msStep));
    };
  }
  const holidaySet = getFinraHolidaySet(finraHolidays);
  const earlyCloseSet = getFinraEarlyCloseSet(finraHolidays);
  return (startMs, endMs, steps) => {
    if (steps <= 0 || endMs <= startMs) {
      return [];
    }
    const sessions = tradingSessionsBetween(startMs, endMs, finraHolidays, holidaySet, earlyCloseSet);
    const total = sessions.reduce((sum, [open, close]) => sum + (close - open), 0);
    if (total <= 0) {
      return [];
    }
    const times: number[] = [];
    let sessionIndex = 0;
    let elapsed = 0; // trading time accumulated before the current session
    for (let i = 0; i < steps; i++) {
      const target = (i + 1) * total / (steps + 1);
      while (
        sessionIndex < sessions.length - 1 &&
        elapsed + (sessions[sessionIndex][1] - sessions[sessionIndex][0]) <= target
      ) {
        elapsed += sessions[sessionIndex][1] - sessions[sessionIndex][0];
        sessionIndex++;
      }
      times.push(Math.round(sessions[sessionIndex][0] + (target - elapsed)));
    }
    return times;
  };
}

// Breakpoints mapping calendar ms -> cumulative trading time (ms) across [startMs, endMs]. Inside a
// session the key advances 1:1 with ms; closed periods (nights, weekends, FINRA holidays) are
// compressed to a small fixed SESSION_GAP_MS, so interpolating over these points produces an x-axis
// that collapses non-trading-hour time while still giving each day's close and the next session's
// open distinct x values (a slope between sessions rather than a vertical jump at one x). Returns []
// until the FINRA calendar loads, or when the span is empty.
export function getTradingTimeAxisPoints(startMs: number, endMs: number, finraHolidays: FinraHolidays): { ms: number, key: number }[] {
  if (!finraHolidays || endMs <= startMs) {
    return [];
  }
  const holidaySet = getFinraHolidaySet(finraHolidays);
  const earlyCloseSet = getFinraEarlyCloseSet(finraHolidays);
  const sessions = tradingSessionsBetween(startMs, endMs, finraHolidays, holidaySet, earlyCloseSet);
  const points: { ms: number, key: number }[] = [];
  let cumulative = 0;
  sessions.forEach(([open, close], i) => {
    if (i > 0) {
      cumulative += SESSION_GAP_MS; // separate the prior close from this open so they don't share an x
    }
    points.push({ ms: open, key: cumulative });
    cumulative += close - open;
    points.push({ ms: close, key: cumulative });
  });
  return points;
}

// Builds the breakpoints for the "Evenly Spaced Trades" x-axis: each trade sits at its integer index
// (0..n-1), with an optional head point just left of the first trade (key -0.4) at the earliest static
// inference time, and a tail point just right of the last trade (key n-0.6) at the latest inference time.
// The head/tail are added only when strictly outside the trade range, so the result is always strictly
// ascending by ms -- the precondition for interpolateBreakpoints' binary search. (A head/tail at or
// inside the range -- e.g. the last trade falling outside trading hours and being filtered out of the
// static times -- would otherwise leave the array unsorted or duplicated by ms.)
export function getEvenlySpacedAxisPoints(
  tradeTimes: number[],
  firstStaticMs: number | undefined,
  latestInferenceMs: number,
): { ms: number, key: number }[] {
  const points: { ms: number, key: number }[] = [];
  const firstTradeMs = tradeTimes.length ? tradeTimes[0] : Number.MAX_SAFE_INTEGER;
  if (firstStaticMs !== undefined && firstStaticMs < firstTradeMs) {
    points.push({ ms: firstStaticMs, key: -0.4 });
  }
  tradeTimes.forEach((ms, i) => points.push({ ms, key: i }));
  const lastTradeMs = tradeTimes.length ? tradeTimes[tradeTimes.length - 1] : Number.MIN_SAFE_INTEGER;
  if (latestInferenceMs > lastTradeMs) {
    points.push({ ms: latestInferenceMs, key: tradeTimes.length - 0.6 });
  }
  return points;
}

// Piecewise-linear interpolation over breakpoints that are sorted ascending by both fields. `xOf`/
// `yOf` pick which field is the input/output, so it works in either direction. `fallback` is returned
// only when there are no breakpoints; a single breakpoint maps every input to that point's value.
export function interpolateBreakpoints(
  points: { ms: number, key: number }[],
  x: number,
  xOf: (p: { ms: number, key: number }) => number,
  yOf: (p: { ms: number, key: number }) => number,
  fallback: number
): number {
  if (points.length === 0) return fallback; // no breakpoints to map against
  // a single breakpoint maps every x to that point's value (the clamps below would also yield this,
  // but make it explicit): otherwise a 1-point single-point-chart domain would fall back to an
  // unrelated value (e.g. loadTime) for tick/tooltip labels instead of the lone point's real mapping
  if (points.length === 1) return yOf(points[0]);
  if (x <= xOf(points[0])) return yOf(points[0]);
  const last = points[points.length - 1];
  if (x >= xOf(last)) return yOf(last);
  // points are sorted ascending by xOf, so binary-search the bracketing pair rather than scanning --
  // on multi-year ranges `points` can be tens of thousands of entries and this runs per data point.
  // Invariant: xOf(points[lo]) <= x < xOf(points[hi]); loop narrows until they're adjacent.
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >>> 1;
    if (xOf(points[mid]) <= x) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const lower = points[lo];
  const upper = points[hi];
  return xOf(lower) === xOf(upper)
    ? yOf(upper)
    : yOf(lower) + (x - xOf(lower)) / (xOf(upper) - xOf(lower)) * (yOf(upper) - yOf(lower));
}

export type TradingTimeScale = {
  toKey: (ms: number) => number; // calendar ms -> cumulative-trading-time axis key
  toMs: (key: number) => number; // axis key -> calendar ms (for tick/tooltip labels)
  min: number;
  max: number;
};

// Builds a calendar-ms <-> cumulative-trading-time mapping over [startMs, endMs] from
// getTradingTimeAxisPoints, so an x-axis positioned by `toKey` compresses non-trading-hour time
// (nights, weekends, FINRA holidays) to a small fixed gap between sessions. Until the FINRA calendar
// has loaded, for an empty span, or for a range too large to build (past getTradingTimeAxisPoints'
// day guard), it falls back to identity (calendar ms) so callers behave as before instead of crashing.
export function createTradingTimeScale(startMs: number, endMs: number, finraHolidays: FinraHolidays): TradingTimeScale {
  let points: { ms: number, key: number }[];
  try {
    points = getTradingTimeAxisPoints(startMs, endMs, finraHolidays);
  } catch (e) {
    // getTradingTimeAxisPoints throws past its ~68-year day guard. Degrade to the identity scale below
    // rather than crashing the chart render; surface the bad range so it still gets noticed.
    console.error('createTradingTimeScale: range too large, falling back to identity scale', e);
    points = [];
  }
  if (points.length < 2) {
    return { toKey: ms => ms, toMs: key => key, min: startMs, max: endMs };
  }
  return {
    toKey: (ms: number) => interpolateBreakpoints(points, ms, p => p.ms, p => p.key, ms),
    toMs: (key: number) => interpolateBreakpoints(points, key, p => p.key, p => p.ms, key),
    min: points[0].key,
    max: points[points.length - 1].key,
  };
}

export function getFormattedTimeToMaturity(maturity: string) {
  // diff in years
  const diffInYears = DateTime.fromJSDate(new Date(maturity)).diffNow('years').years
  if (diffInYears >= 1) {
    return roundToDecimals(diffInYears, 1) + 'y';
  }

  // diff in months if less than 1 year
  const diffInMonths = diffInYears * 12;
  if (diffInMonths >= 1) {
    return roundToDecimals(diffInMonths, 0) + 'm';
  }

  // diff in days if less than 1 month
  const diffInDays = DateTime.fromJSDate(new Date(maturity)).diffNow('days').days
  return roundToDecimals(Math.max(0, diffInDays), 0) + 'd';
}

export const formatDateLong = (m: number) => {
  const d = new Date(m);
  return d.toLocaleString(undefined, { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',  });
};